/**
 * Job status vocabulary for the UI: a thin view over the CANONICAL `shared/job_status.mjs` (P1, master plan C3),
 * which holds India's `order_status` values verbatim plus AWAITING APPROVAL, the US labels and the authority rules.
 * This file only adds UI phases/tones. The UI never computes transitions: every job carries `allowed_actions[]`.
 */
import { ORDER_STATUSES, US_LABELS, US_HIDDEN, AWAITING_APPROVAL, isTerminalStatus, COMPLETED_STATUSES } from '@shared/job_status.mjs';

export { ORDER_STATUSES, US_LABELS, US_HIDDEN, AWAITING_APPROVAL };
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const isCancelled = (s: string) => s.startsWith('CANCELLED');
export const isTerminal = (s: string) => isTerminalStatus(s);
/** Money statuses: never offered as a manual choice anywhere in the UI (booking_status_authority). */
export const MONEY_STATUSES = COMPLETED_STATUSES;

// ─────────────────────────────────────────────────────────── US display (labels + Stage tones)
export type Tone = 'signal' | 'danger' | 'ai' | 'transit' | 'done' | 'neutral';
/** Coarse US phase used by boards, filters and the Stage timeline. */
export type Phase = 'awaiting' | 'booked' | 'assigned' | 'en_route' | 'arrived' | 'in_progress' | 'invoiced' | 'paid' | 'closed' | 'cancelled';

export function phaseOf(s: string): Phase {
  if (s === AWAITING_APPROVAL) return 'awaiting';
  if (isCancelled(s) || s === 'DUPLICATE') return 'cancelled';
  switch (s) {
    case 'CONFIRMED': case 'MECHANIC DENIED': case 'REWORK': case 'CUSTOMER CONFIRMED': case 'LOCATION CONFIRMED': case 'RETRYING IVR ALLOCATION': return 'booked';
    case 'ASSIGNED': case 'REMINDER INITIATED': case 'MECHANIC ALLOCATED': case 'MECHANIC CONFIRMED': case 'CUSTOMER CALLED': case 'MECHANIC CALLED CUSTOMER':
    case 'CUSTOMER PHONE RINGING': case 'CUSTOMER ANSWERED': case 'CUSTOMER DID NOT ANSWER': case 'NO RESPONSE': return 'assigned';
    case 'CUSTOMER DETAILS SENT': case 'MECHANIC DID NOT REACH': case 'MECHANIC DISCONNECTED': return 'en_route';
    case 'MECHANIC REACHED': case 'IN PROGRESS - AUTO': return 'arrived';
    case 'SERVICE STARTED': case 'IN PROGRESS': case 'IN PROGRESS - VEHICLE TOWED': case 'IN PROGRESS - BATTERY TOWED': return 'in_progress';
    case 'INVOICE GENERATED': case 'NO PAYMENT RECEIVED': return 'invoiced';
    case 'COMPLETED': case 'CASH TAKEN': return 'paid';
    case 'SETTLED': return 'closed';
  }
  return 'booked';
}

export const PHASE_LABEL: Record<Phase, string> = {
  awaiting: 'Needs your OK', booked: 'Booked', assigned: 'Assigned', en_route: 'En route', arrived: 'On site',
  in_progress: 'In progress', invoiced: 'Invoiced', paid: 'Paid', closed: 'Settled', cancelled: 'Cancelled',
};
export const PHASE_TONE: Record<Phase, Tone> = {
  awaiting: 'signal', booked: 'ai', assigned: 'ai', en_route: 'transit', arrived: 'transit', in_progress: 'transit',
  invoiced: 'transit', paid: 'done', closed: 'done', cancelled: 'neutral',
};
/** The Stage job timeline, in US phases. */
export const TIMELINE: Phase[] = ['booked', 'assigned', 'en_route', 'arrived', 'in_progress', 'invoiced', 'paid'];

/** US label for a raw status (canonical labels from shared/job_status.mjs). */
export const statusLabel = (s: string): string => (US_LABELS as Record<string, string>)[s] ?? s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
export const statusTone = (s: string): Tone => PHASE_TONE[phaseOf(s)];

/** Human label for an `allowed_actions[]` key (the server owns the list; unknown keys are title-cased). */
const ACTION_LABEL: Record<string, string> = {
  assign: 'Assign', reassign: 'Reassign', unassign: 'Unassign', confirm: 'Confirm', approve: 'Approve',
  send_estimate: 'Send estimate', start: 'Start work', complete: 'Mark work done', invoice: 'Send invoice', pay_link: 'Send pay link',
  record_cash: 'Record offline payment', reschedule: 'Reschedule', cancel: 'Cancel job', no_show: 'No-show', hold: 'Put on hold',
  override_status: 'Override status', duplicate: 'Mark duplicate', note: 'Add note',
};
export const actionLabel = (a: string) => ACTION_LABEL[a] ?? a.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
